/**
 * The console's sticky header: which source, which view, and the run's
 * provenance.
 *
 * Replaces the old `ChainTabs` — same "worst score never hidden" guarantee,
 * carried by `tabLabelParts`, now rendered through the shared `SegmentedControl`
 * instead of a hand-rolled tablist.
 */

import SegmentedControl, { type SegmentedOption } from '../../ui/SegmentedControl';
import type { ValidationChain } from '../../../types/dataQuality';
import type { ConsoleView } from './hooks/useDataQualityConsole';
import { chainSource, pdfProvenance, tabLabelParts } from './dqSelectors';

export interface ConsoleCommandBarProps {
  chains: ValidationChain[];
  source: string;
  onSourceChange: (source: string) => void;
  view: ConsoleView;
  onViewChange: (view: ConsoleView) => void;
  availableViews: ConsoleView[];
}

const VIEW_LABEL: Record<ConsoleView, string> = {
  summary: 'Summary',
  findings: 'Findings',
  fields: 'Fields',
  compare: 'Compare',
};

export default function ConsoleCommandBar({
  chains,
  source,
  onSourceChange,
  view,
  onViewChange,
  availableViews,
}: ConsoleCommandBarProps) {
  const activeChain = chains.find((c) => chainSource(c) === source);

  const sourceOptions: SegmentedOption<string>[] = chains.map((chain) => {
    const parts = tabLabelParts(chain);
    return {
      value: chainSource(chain),
      ariaLabel: `${parts.source}, quality score ${parts.score || 'unavailable'}${
        parts.needsAttention ? ', has findings' : ''
      }`,
      label: (
        <span className="flex items-baseline gap-2">
          <span>{parts.source}</span>
          {parts.years && <span className="text-[11px] opacity-60">{parts.years}</span>}
        </span>
      ),
      trailing: (
        <span className="flex items-center gap-1">
          {parts.score && <span className="font-mono tabular-nums">{parts.score}</span>}
          {parts.needsAttention && (
            <span className="text-amber-500" aria-hidden="true">
              ⚠
            </span>
          )}
        </span>
      ),
    };
  });

  const viewOptions: SegmentedOption<ConsoleView>[] = availableViews.map((v) => ({
    value: v,
    label: VIEW_LABEL[v],
  }));

  const provenance = activeChain ? pdfProvenance(activeChain) : null;

  return (
    <div
      // Offset by the page tab bar's MEASURED height (published by
      // CompanyDetail as `--dq-sticky-top`), not a constant: that nav wraps to
      // two rows at laptop widths, and a hardcoded offset left this bar
      // painting underneath it — at a lower z-index, so it vanished on scroll.
      className="sticky z-10 -mx-4 border-b border-neutral-200 bg-white/95 px-4 py-3 backdrop-blur-sm"
      style={{ top: 'var(--dq-sticky-top, 0px)' }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        {chains.length > 1 ? (
          <SegmentedControl
            options={sourceOptions}
            value={source}
            onChange={onSourceChange}
            ariaLabel="Validation source"
            size="sm"
          />
        ) : (
          // One source is not a choice, but it still carries its score and its
          // year span — the switcher is what is unnecessary here, not the
          // information on it.
          (() => {
            const parts = tabLabelParts(chains[0]);
            return (
              <span className="flex items-baseline gap-2 text-sm">
                <span className="font-semibold text-neutral-800">{parts.source}</span>
                {parts.years && (
                  <span className="text-[11px] text-neutral-400">{parts.years}</span>
                )}
                {parts.score && (
                  <span className="font-mono text-xs tabular-nums text-neutral-700">
                    {parts.score}
                  </span>
                )}
                {parts.needsAttention && (
                  <span className="text-amber-500" aria-label="has findings">
                    ⚠
                  </span>
                )}
              </span>
            );
          })()
        )}

        <SegmentedControl
          options={viewOptions}
          value={view}
          onChange={onViewChange}
          ariaLabel="Console view"
          variant="underline"
          size="sm"
        />
      </div>

      {activeChain && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-400">
          <span>scoring {activeChain.summary.scoring_version ?? '—'}</span>
          <span>run {activeChain.run_number}</span>
          {provenance && <span>{provenance}</span>}
        </div>
      )}
    </div>
  );
}
